import { getActiveLayoutStores } from './layout-store/registry'
import { COMPONENT_META } from './components/add-panel-menu-constants'

/**
 * Component types whose header shows a ticker pill — mirrors
 * `TICKER_SEARCH_TYPES` in `components/panel-header.tsx` (kept as a local
 * const there since panel-header.tsx is a component file and must only
 * export components, per Fast Refresh rules). Only these types get a ticker
 * suffix in the resolved title.
 */
const TICKER_TYPES = new Set<string>([
  'chart',
  'key-stats',
  'news',
  'ticker-stats',
  'history-chart',
  'history-news',
  'backtest-trade-chart',
])

/**
 * Resolve a human-readable title for a panel by id — e.g. "Chart — AAPL" for
 * ticker-scoped panels, or just the component label ("Scanner") otherwise.
 *
 * Used by the chat @-mention flow and the panel "Ask AI" menu item. Both only
 * have a `panelId` to work from, and neither is guaranteed to be inside any
 * particular workspace's `LayoutStoreProvider` (chat is mounted globally in
 * app-layout, outside the per-page workspace tree) — so this walks the
 * module-level layout-store registry (`layout-store/registry.ts`) instead of
 * a React hook. Falls back to the panelId itself if no active store knows it
 * (e.g. the panel was removed between mention and send).
 */
function findPanelInfo(panelId: string): { componentType: string; title: string } | null {
  for (const store of getActiveLayoutStores()) {
    const state = store.getState()
    const config = state.windowConfigs[panelId]
    if (!config) continue

    const label = COMPONENT_META[config.componentType]?.label ?? config.componentType
    if (!TICKER_TYPES.has(config.componentType)) {
      return { componentType: config.componentType, title: label }
    }

    const symbols = (state.globalSettings.symbols as string[] | undefined) ?? []
    const ticker = symbols[config.symbolIndex]
    return { componentType: config.componentType, title: ticker ? `${label} — ${ticker}` : label }
  }
  return null
}

export function resolvePanelTitle(panelId: string): string {
  return findPanelInfo(panelId)?.title ?? panelId
}

/** The panel's componentType, e.g. for building a PanelMention. Undefined if
 *  no active layout store knows this panelId. */
export function resolvePanelComponentType(panelId: string): string | undefined {
  return findPanelInfo(panelId)?.componentType
}
